<script setup lang="ts">
import { ref } from 'vue';
import { useForm, useFieldArray } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import {
  useContactDetails,
  useToastNotifications,
} from '@apparatix/composables';
import { contactSchema } from '@apparatix/schemas/Contact';
import { useContactStore } from '@apparatix/stores/useContactStore.ts';
import ContactDetailViewForm from '@apparatix/leads/ContactDetailViewForm.vue';
import DetailEditorControls from '@apparatix/library/DetailEditorControls.vue';
import ContactDetailEditorForm from '@apparatix/leads/ContactDetailEditorForm.vue';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';

const store = useContactStore();
const { showSuccess } = useToastNotifications();
const confirm = useConfirm();

const getDefaultAddress = () => {
  if (!store.contactDetails.addresses[0]) {
    return [{
      addressLine1: '',
      addressLine2: '',
      city: '',
      region: '',
      postalCode: '',
    }];
  }
  return store.contactDetails.addresses;
};

const {
  defineField, resetForm, errors: detailEditorErrors, values: detailEditorModel, meta,
} = useForm({
  validationSchema: toTypedSchema(contactSchema),
  initialValues: {
    id: store.contactDetails.id,
    salutation: store.contactDetails.salutation,
    firstName: store.contactDetails.firstName,
    middleName: store.contactDetails.middleName,
    lastName: store.contactDetails.lastName,
    suffix: store.contactDetails.suffix,
    nickname: store.contactDetails.nickname,
    jobTitle: store.contactDetails.jobTitle,
    companyName: store.contactDetails.companyName,
    emails: store.contactDetails.emails,
    phoneNumbers: store.contactDetails.phoneNumbers,
    webPages: store.contactDetails.webPages,
    addresses: getDefaultAddress(),
    owner: store.contactDetails.owner,
  },
});

const detail = useContactDetails();

const [salutation] = defineField('salutation');
const [firstName] = defineField('firstName');
const [middleName] = defineField('middleName');
const [lastName] = defineField('lastName');
const [suffix] = defineField('suffix');
const [nickname] = defineField('nickname');
const [jobTitle] = defineField('jobTitle');
const [companyName] = defineField('companyName');
const [addresses] = defineField('addresses');
const [owner] = defineField('owner');
const { remove: emailRemove, push: emailPush, fields: emails } = useFieldArray('emails');
const { remove: phoneRemove, push: phonePush, fields: phoneNumbers } = useFieldArray('phoneNumbers');
const { remove: webPageRemove, push: webPagePush, fields: webPages } = useFieldArray('webPages');

const isEditing = ref(false);

const onSave = async () => {
  await detail.sendContactUpdate(detailEditorModel);
  showSuccess('Contact Saved', 'Success');
  isEditing.value = false;
  resetForm({
    values: {
      id: store.contactDetails.id,
      salutation: store.contactDetails.salutation,
      firstName: store.contactDetails.firstName,
      middleName: store.contactDetails.middleName,
      lastName: store.contactDetails.lastName,
      suffix: store.contactDetails.suffix,
      nickname: store.contactDetails.nickname,
      jobTitle: store.contactDetails.jobTitle,
      companyName: store.contactDetails.companyName,
      emails: store.contactDetails.emails,
      phoneNumbers: store.contactDetails.phoneNumbers,
      webPages: store.contactDetails.webPages,
      owner: store.contactDetails.owner,
      addresses: getDefaultAddress(),
    },
  });
};

const onCancelEditing = () => {
  if (meta.value.dirty) {
    confirm.require({
      group: 'confirmClose',
      message: 'You have unsaved changes. Do you want to continue?',
      header: ' ',
      accept: () => onConfirmCancel(),
      position: 'center',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectClass: 'p-button-secondary',
    });
    return;
  }
  onConfirmCancel();
};

const onConfirmCancel = () => {
  resetForm();
  isEditing.value = false;
};

const onAddRow = (type) => {
  if (type === 'email') {
    emailPush({ email: '' });
    return;
  }
  if (type === 'phone') {
    phonePush({ number: '', addressType: 'Work' });
    return;
  }
  if (type === 'webPage') {
    webPagePush({ url: '', addressType: 'Other' });
    return;
  }
};

const onRemoveRow = (type, idx) => {
  if (type === 'email') {
    emailRemove(idx);
    return;
  }
  if (type === 'phone') {
    phoneRemove(idx);
    return;
  }
  if (type === 'webPage') {
    webPageRemove(idx);
    return;
  }
};

</script>
<template>
  <DetailEditorControls
    header="Details"
    :is-editing="isEditing"
    :disable-save="Boolean(Object.keys(detailEditorErrors).length) || !meta.dirty"
    @on:edit-click="isEditing = true"
    @on:cancel-click="onCancelEditing"
    @on:save-click="onSave"
  >
    <ContactDetailViewForm
      v-if="!isEditing"
      :contact-details="detailEditorModel"
      :errors="detailEditorErrors"
    />
    <ContactDetailEditorForm
      v-if="isEditing"
      v-model:salutation="salutation"
      v-model:first-name="firstName"
      v-model:middle-name="middleName"
      v-model:last-name="lastName"
      v-model:suffix="suffix"
      v-model:nickname="nickname"
      v-model:job-title="jobTitle"
      v-model:company-name="companyName"
      v-model:addresses="addresses"
      v-model:phone-numbers="phoneNumbers"
      v-model:web-pages="webPages"
      v-model:emails="emails"
      v-model:owner="owner"
      :errors="detailEditorErrors"
      @on:add-row="onAddRow"
      @on:remove-row="onRemoveRow"
    />
  </DetailEditorControls>
  <ConfirmDialog group="confirmClose" />
</template>
